// src/utils/Onboarding/HairSegmentation.js

import { ImageSegmenter, FilesetResolver } from '@mediapipe/tasks-vision';

/**
 * MEDIAPIPE HAIR SEGMENTATION UTILITIES
 *
 * Singleton ImageSegmenter (HairSegmenter model).
 * Exposes:
 *  - segmentHair(image): returns masks (hair + background when available)
 *  - buildHairOverlayCanvas(...): visual tint
 *  - computeHairBoundingBoxOriginal(...): bbox in ORIGINAL pixels (kept for compatibility)
 *  - computePrimaryHairBoundingBoxOriginal(...): bbox of the LARGEST hair blob in ORIGINAL pixels (preferred)
 *  - getHairMaskStats(...): numbers for on-screen debugging
 */

// Singleton
let segmenter = null;
let isInitializing = false;
let initError = null;

/* ===========================
   Initialization
   =========================== */

export async function initializeHairSegmenter() {
  if (segmenter) return segmenter;
  if (isInitializing) {
    while (isInitializing) {
      await new Promise(r => setTimeout(r, 100));
    }
    return segmenter;
  }
  try {
    isInitializing = true;

    const vision = await FilesetResolver.forVisionTasks(
      'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm'
    );

    segmenter = await ImageSegmenter.createFromOptions(vision, {
      baseOptions: {
        modelAssetPath:
          'https://storage.googleapis.com/mediapipe-models/image_segmenter/hair_segmenter/float32/latest/hair_segmenter.tflite',
        delegate: 'GPU',
      },
      runningMode: 'IMAGE',
      outputCategoryMask: true,
      outputConfidenceMasks: true,
    });

    initError = null;
    return segmenter;
  } catch (err) {
    initError = err;
    segmenter = null;
    throw err;
  } finally {
    isInitializing = false;
  }
}

/* ===========================
   Segmentation
   =========================== */

export async function segmentHair(imageElement) {
  if (!segmenter) await initializeHairSegmenter();
  if (!segmenter) throw new Error('Hair segmenter unavailable');
  if (!imageElement || !imageElement.complete) {
    throw new Error('Invalid or incomplete image element');
  }

  const result = segmenter.segment(imageElement);

  const confidenceMasks = result.confidenceMasks || null; // [bg, hair] when present
  const bgMask = confidenceMasks && confidenceMasks.length >= 2 ? confidenceMasks[0] : null;
  const hairMask = confidenceMasks && confidenceMasks.length >= 2 ? confidenceMasks[1] : (result.categoryMask || null);

  // maskType indicates how hairMask should be interpreted for overlay building:
  //  - 'confidence' when hairMask is probabilities
  //  - 'category'   when hairMask is uint8 classes (single mask)
  const maskType = (confidenceMasks && confidenceMasks.length >= 2) ? 'confidence'
                    : (result.categoryMask ? 'category' : null);

  const maskWidth  = hairMask ? hairMask.width  : 0;
  const maskHeight = hairMask ? hairMask.height : 0;

  return {
    maskType,
    hairMask,          // MPMask for hair channel (confidence) OR categoryMask (uint8)
    bgMask,            // MPMask for background confidence (may be null)
    maskWidth,
    maskHeight,
    qualityScores: result.qualityScores ?? null,
  };
}

/* ===========================
   Overlay builder
   =========================== */

export function buildHairOverlayCanvas(maskType, mask, tintRGBA = [59,130,246,200], probToAlphaScale = 0.7) {
  if (!mask) return null;

  const w = mask.width;
  const h = mask.height;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  const imageData = ctx.createImageData(w, h);
  const data = imageData.data;

  if (maskType === 'confidence') {
    // Float32 probabilities (hair)
    const probs = mask.getAsFloat32Array();
    const [r, g, b, aMax] = tintRGBA;
    const aScale = (typeof probToAlphaScale === 'number' ? probToAlphaScale : 0.7) * (aMax / 255);

    for (let i = 0; i < probs.length; i++) {
      const p = Math.max(0, Math.min(1, probs[i]));
      const o = i * 4;
      data[o] = r;
      data[o + 1] = g;
      data[o + 2] = b;
      data[o + 3] = Math.round(255 * p * aScale);
    }
  } else if (maskType === 'category') {
    // Uint8 classes: 1 = hair
    const cats = mask.getAsUint8Array();
    const [r, g, b, aMax] = tintRGBA;
    for (let i = 0; i < cats.length; i++) {
      const isHair = cats[i] === 1;
      const o = i * 4;
      data[o] = r;
      data[o + 1] = g;
      data[o + 2] = b;
      data[o + 3] = isHair ? aMax : 0;
    }
  } else {
    return null;
  }

  ctx.putImageData(imageData, 0, 0);
  return canvas;
}

/* ===========================
   Hair bbox & stats
   =========================== */

/**
 * Legacy bbox: include all hair pixels (category==1 OR hairProb>bgProb OR >=0.5).
 * Kept for compatibility, but prefer computePrimaryHairBoundingBoxOriginal(...) below.
 */
export function computeHairBoundingBoxOriginal(maskType, hairMask, imgW, imgH, bgMask = null) {
  if (!maskType || !hairMask || !imgW || !imgH) return null;

  const Mw = hairMask.width;
  const Mh = hairMask.height;
  let minX = Infinity, minY = Infinity, maxX = -1, maxY = -1;

  if (maskType === 'category') {
    const cats = hairMask.getAsUint8Array();
    for (let y = 0; y < Mh; y++) {
      const row = y * Mw;
      for (let x = 0; x < Mw; x++) {
        if (cats[row + x] === 1) {
          if (x < minX) minX = x;
          if (y < minY) minY = y;
          if (x > maxX) maxX = x;
          if (y > maxY) maxY = y;
        }
      }
    }
  } else {
    const hair = hairMask.getAsFloat32Array();
    const hasBg = !!bgMask && bgMask.width === Mw && bgMask.height === Mh;
    const bg = hasBg ? bgMask.getAsFloat32Array() : null;

    for (let y = 0; y < Mh; y++) {
      const row = y * Mw;
      for (let x = 0; x < Mw; x++) {
        const hp = hair[row + x];
        const isHair = hasBg ? (hp > bg[row + x]) : (hp >= 0.5);
        if (isHair) {
          if (x < minX) minX = x;
          if (y < minY) minY = y;
          if (x > maxX) maxX = x;
          if (y > maxY) maxY = y;
        }
      }
    }
  }

  if (maxX < 0 || maxY < 0) return null;

  const bx = (minX / Mw) * imgW;
  const by = (minY / Mh) * imgH;
  const bw = ((maxX - minX + 1) / Mw) * imgW;
  const bh = ((maxY - minY + 1) / Mh) * imgH;

  return { x: bx, y: by, width: bw, height: bh };
}

/**
 * NEW: Primary hair bbox using the LARGEST CONNECTED COMPONENT (8-connected)
 * to ignore tiny stray fragments. Works threshold-free.
 *
 * Options:
 *  - maxLabelSide: downsample so the longer side <= this (default 512) for speed
 *  - minComponentAreaRatio: drop components smaller than this fraction of the downsampled mask (default 0.001 = 0.1%)
 */
export function computePrimaryHairBoundingBoxOriginal(
  maskType,
  hairMask,
  imgW,
  imgH,
  bgMask = null,
  opts = {}
) {
  if (!maskType || !hairMask || !imgW || !imgH) return null;

  const Mw = hairMask.width;
  const Mh = hairMask.height;

  const {
    maxLabelSide = 512,
    minComponentAreaRatio = 0.001
  } = opts;

  // Decide hair pixels threshold-free
  const cats = maskType === 'category' ? hairMask.getAsUint8Array() : null;
  const hair = maskType === 'confidence' ? hairMask.getAsFloat32Array() : null;
  const hasBg = maskType === 'confidence' && bgMask && bgMask.width === Mw && bgMask.height === Mh;
  const bg = hasBg ? bgMask.getAsFloat32Array() : null;

  // Downsample step
  const step = Math.max(1, Math.ceil(Math.max(Mw, Mh) / maxLabelSide));
  const dsW = Math.max(1, Math.floor(Mw / step));
  const dsH = Math.max(1, Math.floor(Mh / step));

  // Build downsampled binary hair map: mark a cell as hair if ANY pixel in the block is hair.
  const bin = new Uint8Array(dsW * dsH);
  for (let y = 0; y < dsH; y++) {
    const y0 = y * step;
    const y1 = Math.min(Mh, y0 + step);
    for (let x = 0; x < dsW; x++) {
      const x0 = x * step;
      const x1 = Math.min(Mw, x0 + step);

      let anyHair = false;

      if (maskType === 'category') {
        for (let yy = y0; yy < y1 && !anyHair; yy++) {
          const row = yy * Mw;
          for (let xx = x0; xx < x1; xx++) {
            if (cats[row + xx] === 1) { anyHair = true; break; }
          }
        }
      } else {
        for (let yy = y0; yy < y1 && !anyHair; yy++) {
          const row = yy * Mw;
          for (let xx = x0; xx < x1; xx++) {
            const hp = hair[row + xx];
            const isHair = hasBg ? (hp > bg[row + xx]) : (hp >= 0.5);
            if (isHair) { anyHair = true; break; }
          }
        }
      }

      bin[y * dsW + x] = anyHair ? 1 : 0;
    }
  }

  // Connected-components (8-neighborhood) on downsampled grid
  const visited = new Uint8Array(dsW * dsH);
  let bestArea = 0;
  let bestBBox = null;
  const areaThreshold = Math.max(1, Math.floor(minComponentAreaRatio * dsW * dsH));
  const stackX = new Int32Array(dsW * dsH);
  const stackY = new Int32Array(dsW * dsH);

  const neighbors = [
    [-1, -1], [0, -1], [1, -1],
    [-1,  0],          [1,  0],
    [-1,  1], [0,  1], [1,  1],
  ];

  for (let y = 0; y < dsH; y++) {
    for (let x = 0; x < dsW; x++) {
      const idx = y * dsW + x;
      if (bin[idx] === 0 || visited[idx] === 1) continue;

      // Flood fill
      let sp = 0;
      stackX[sp] = x; stackY[sp] = y; sp++;
      visited[idx] = 1;

      let minX = x, minY = y, maxX = x, maxY = y, area = 0;

      while (sp > 0) {
        sp--;
        const cx = stackX[sp], cy = stackY[sp];
        area++;
        if (cx < minX) minX = cx;
        if (cy < minY) minY = cy;
        if (cx > maxX) maxX = cx;
        if (cy > maxY) maxY = cy;

        for (let k = 0; k < 8; k++) {
          const nx = cx + neighbors[k][0];
          const ny = cy + neighbors[k][1];
          if (nx < 0 || nx >= dsW || ny < 0 || ny >= dsH) continue;
          const nidx = ny * dsW + nx;
          if (visited[nidx] === 0 && bin[nidx] === 1) {
            visited[nidx] = 1;
            stackX[sp] = nx; stackY[sp] = ny; sp++;
          }
        }
      }

      if (area >= areaThreshold && area > bestArea) {
        bestArea = area;
        bestBBox = { minX, minY, maxX, maxY };
      }
    }
  }

  if (!bestBBox) return null;

  // Map downsampled bbox back to mask coordinates
  const minXmask = bestBBox.minX * step;
  const minYmask = bestBBox.minY * step;
  const maxXmask = Math.min(Mw - 1, (bestBBox.maxX + 1) * step - 1);
  const maxYmask = Math.min(Mh - 1, (bestBBox.maxY + 1) * step - 1);

  // Map mask-space bbox to original image-space bbox
  const bx = (minXmask / Mw) * imgW;
  const by = (minYmask / Mh) * imgH;
  const bw = ((maxXmask - minXmask + 1) / Mw) * imgW;
  const bh = ((maxYmask - minYmask + 1) / Mh) * imgH;

  return { x: bx, y: by, width: bw, height: bh };
}

/**
 * Lightweight stats for on-screen debugging.
 */
export function getHairMaskStats(maskType, hairMask) {
  if (!maskType || !hairMask) return null;
  const Mw = hairMask.width;
  const Mh = hairMask.height;

  if (maskType === 'confidence') {
    const probs = hairMask.getAsFloat32Array();
    let sum = 0, max = 0;
    for (let i = 0; i < probs.length; i++) {
      const p = probs[i];
      sum += p;
      if (p > max) max = p;
    }
    const mean = probs.length ? sum / probs.length : 0;
    return { type: 'confidence', mean, max, width: Mw, height: Mh };
  } else {
    const cats = hairMask.getAsUint8Array();
    let hairCount = 0;
    for (let i = 0; i < cats.length; i++) {
      if (cats[i] === 1) hairCount++;
    }
    const coverage = cats.length ? hairCount / cats.length : 0;
    return { type: 'category', coverage, width: Mw, height: Mh };
  }
}

/* ===========================
   Status & cleanup
   =========================== */

export function getHairSegmentationStatus() {
  return {
    isInitialized: !!segmenter,
    isInitializing,
    error: initError,
  };
}

export function cleanupHairSegmenter() {
  try {
    if (segmenter) segmenter.close();
  } catch (e) {}
  segmenter = null;
  initError = null;
  isInitializing = false;
}
